/**
 * The price of easyrechnung, in one place.
 *
 * Moved here from `apps/site/shared/pricing.ts` when billing arrived in the
 * API: the site states these figures, the API charges them, and the trial
 * length now gates access — so the authority sits in the shared package and
 * both apps derive from it. `apps/site/shared/pricing.ts` re-exports this
 * module; `pricing.test.ts` there still greps the pages for stray literals.
 *
 * The decision behind the figures, so it survives without a second document:
 * one price, stated net only, because „netto" already says what „zzgl. USt"
 * would and every competitor page quotes net. 100 € is an introductory price
 * for the first year and not a permanent rate, and it ends on a date rather
 * than on a countdown — a clock on a price reads as pressure to this audience.
 * The rest of the reasoning is in `docs/research/WEBSITE.md`.
 *
 * Amounts are integer cents, per the house rule in `CLAUDE.md`.
 */
import { getTaxLaw } from './taxlaw/index.js';
import { toCents, vatFromNet } from './money.js';
/** Net price per year, regular. 120 €. */
export const PRICE_NET_CENTS = toCents(120);
/**
 * Net price for the first year when ordered by `INTRO_PRICE_UNTIL`. 100 €.
 * From the second year on, `PRICE_NET_CENTS` applies — this is an introductory
 * price for year one, not a permanent early-bird rate.
 */
export const INTRO_PRICE_NET_CENTS = toCents(100);
/** Last day the introductory price can be claimed, `YYYY-MM-DD`. */
export const INTRO_PRICE_UNTIL = '2026-10-31';
/**
 * The year whose rules the price derives from. The site is prerendered, so
 * this is fixed at build time; in the API it is the year the process started,
 * which is equally right — a rebuild or restart is what moves it on.
 */
export const PRICED_YEAR = new Date().getFullYear();
/**
 * Umsatzsteuer on the subscription: Inland, Normalsteuersatz (§ 10 Abs 1 UStG).
 * Read from the rule set rather than typed here a second time —
 * `pricing.test.ts` cross-checks it against `getTaxLaw()`.
 */
export const VAT_RATE = getTaxLaw(PRICED_YEAR).vat.standardRate;
/**
 * Days the product can be used without paying and without payment details.
 * The owner may change this; it exists exactly once so that changing it is one
 * edit. It must never be written as a literal in a template or a copy string.
 */
export const TRIAL_DAYS = 30;
/** Where the primary call to action goes. The product itself lives here. */
export const APP_URL = 'https://app.easyrechnung.at';
/**
 * Where „kostenlos testen" goes, everywhere it is offered.
 *
 * The bare `APP_URL` is the door for somebody who already has an account: it
 * lands on the login mask. Somebody who has just read that they can try this
 * for {@link TRIAL_DAYS} days has no account yet, and the login mask asks them
 * for one they cannot have — so the trial skips ahead to the registration form.
 * Stated once, because it was written out in fourteen templates and only the
 * header had it right.
 */
export const REGISTER_URL = `${APP_URL}/register`;
/**
 * Adds Umsatzsteuer to a net amount in cents.
 *
 * `netCents` is always a whole number, so `net + vatFromNet(net, rate)` and
 * `Math.round(net * (1 + rate))` round to the identical cent — adding an
 * integer never changes which side of `.5` the fractional part falls on.
 * This form reuses the house VAT helper instead of a second rounding rule.
 */
export function grossCents(netCents) {
    return netCents + vatFromNet(netCents, VAT_RATE);
}
/** 144 € — what actually gets charged at the regular price. */
export const PRICE_GROSS_CENTS = grossCents(PRICE_NET_CENTS);
/** 120 € — what actually gets charged in the first year, with the intro price. */
export const INTRO_PRICE_GROSS_CENTS = grossCents(INTRO_PRICE_NET_CENTS);
//# sourceMappingURL=pricing.js.map